import React from 'react';

import { Money, formatMoney, parseMoney } from '@betterment/core';
import { useGraphQLQuery } from '@betterment/graphql';
import {
  Button,
  FormikField,
  GuidanceBox,
  H1,
  H3,
  HStack,
  RadioGroup,
  Separator,
  Text,
  VStack,
} from '@betterment/samba-react';
import { WorkflowLayout } from '@betterment/shared-components';
import { Form, Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { CoverageSelectionStepOptionsDocument } from './graphql/query.coverageSelectionStepOptions.generated';

import { coverage, useEmergencyFundStep } from '../FlowConfig';

const schema = Yup.object({
  selectedCoverage: coverage.fields.selectedCoverage,
});

type FormValues = Yup.InferType<typeof schema>;

export const CoverageSelectionStep: React.FC = () => {
  const navigate = useNavigate();
  const { commit, dependencies } = useEmergencyFundStep('coverage');
  const { flowId } = useParams<{ flowId: string }>();

  const monthlyExpensesCents = parseMoney(dependencies.monthlyExpenses.monthlyExpensesAmount)?.cents ?? 0;

  const { data } = useGraphQLQuery(CoverageSelectionStepOptionsDocument, {
    variables: { id: 'emergency_fund:emergency_2', monthlyExpensesCents },
    throwOnError: true,
  });

  if (!data || data.purposeCreationOption.__typename !== 'EmergencyFundPurposeCreationOption') return null;

  const {
    emergencyFundCoverage: { buffer, options, messages, description },
  } = data.purposeCreationOption;

  const handleSubmit = (formValues: FormValues) => {
    if (formValues.selectedCoverage === 'custom') {
      commit({
        targetAmount: Money(0),
        bufferPercentage: 0,
        selectedCoverage: 'custom',
      });
      void navigate(`/add_purpose/emergency_fund/${flowId}/custom_coverage`);
      return;
    }

    const opt = options.find(option => String(option.months) === formValues.selectedCoverage);
    commit({
      targetAmount: opt?.amount || Money(0),
      bufferPercentage: buffer,
      // @ts-ignore Validation assures us that this is correct but the typing is impossible.
      selectedCoverage: formValues.selectedCoverage,
    });
    void navigate(`/add_purpose/emergency_fund/${flowId}/next_step`);
  };

  return (
    <WorkflowLayout pageTitle="Emergency Fund Coverage">
      <WorkflowLayout.Header workflowTitle={null} onClose={() => navigate('/')} onBack={() => navigate(-1)} />
      <WorkflowLayout.Content>
        <VStack css={{ gap: 'space.300' }}>
          <VStack css={{ gap: 'space.200', mb: 'space.400' }}>
            <H1 css={{ textWrap: 'auto' }}>How much do you want to save?</H1>
            <Text>{description}</Text>
          </VStack>

          <Formik
            onSubmit={handleSubmit}
            initialValues={{ selectedCoverage: '' }}
            validationSchema={schema}
            validateOnChange={false}
          >
            <Form>
              <FormikField name="selectedCoverage">
                <FormikField.Control>
                  <RadioGroup>
                    {options.map(option => (
                      <React.Fragment key={option.id}>
                        <Separator />
                        <RadioGroup.ItemContainer>
                          <RadioGroup.Item value={`${option.months}`} />
                          <RadioGroup.ItemLabel css={{ flex: 1 }}>
                            <HStack
                              css={{
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                my: 'space.300',
                              }}
                            >
                              <VStack css={{ gap: 'space.0', ml: 'space.150' }}>
                                <Text as="span">
                                  {option.months} months expenses + {buffer}%
                                </Text>
                                <Text as="span" variant="text50" color="text.weak">
                                  {option.bufferedMonths} months expenses
                                </Text>
                              </VStack>
                              <Text as="span">{formatMoney(option.amount, 'defaultDollar')}</Text>
                            </HStack>
                          </RadioGroup.ItemLabel>
                        </RadioGroup.ItemContainer>
                      </React.Fragment>
                    ))}
                    <Separator />
                    <RadioGroup.ItemContainer>
                      <RadioGroup.Item value="custom" />
                      <RadioGroup.ItemLabel css={{ flex: 1 }}>
                        <HStack
                          css={{
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            width: '100%',
                            my: 'space.300',
                          }}
                        >
                          <VStack css={{ gap: 'space.0', ml: 'space.150' }}>
                            <Text as="span">Custom amount</Text>
                          </VStack>
                        </HStack>
                      </RadioGroup.ItemLabel>
                    </RadioGroup.ItemContainer>
                    <Separator />
                  </RadioGroup>
                </FormikField.Control>
              </FormikField>

              <GuidanceBox css={{ maxWidth: '100%', mt: 'space.400' }}>
                <GuidanceBox.Icon />
                <GuidanceBox.Header>
                  <H3 variant="title100">{messages[0].title}</H3>
                </GuidanceBox.Header>
                <GuidanceBox.Content css={{ display: 'grid', gap: 'space.100' }}>
                  <Text dangerouslySetInnerHTML={{ __html: messages[0].body }} />
                </GuidanceBox.Content>
              </GuidanceBox>
              <HStack css={{ mt: 'space.400' }}>
                <Button type="submit" css={{ width: 'fit-content' }}>
                  Continue
                </Button>
                <Button
                  variant="ghostPrimary"
                  onClick={() => void navigate(`/add_purpose/emergency_fund/${flowId}/next_step`)}
                >
                  Skip for now
                </Button>
              </HStack>
            </Form>
          </Formik>
        </VStack>
      </WorkflowLayout.Content>
    </WorkflowLayout>
  );
};
